/**
 * ToneAudioBaseNode - base class for Tone.js audio nodes
 *
 * Extends BaseNode with Tone.js-specific functionality:
 * - Audio endpoint proxy with Tone.js connect bridging
 * - Access to Tone.js library via deps
 * - AudioContext from Tone.js context
 */

import { BaseNode } from '@nodes/BaseNode';
import type { EndpointsRecord, TypedAudio } from '@nodes/TypedEndpoints';
import type { ToneJsDependencies, ToneLibrary } from '../tonejsPlugin';
import type { Payload } from '@/graph/Payload';

export abstract class ToneAudioBaseNode<T extends EndpointsRecord = EndpointsRecord> extends BaseNode<T> {
  protected tone: ToneLibrary;
  protected audioContext!: AudioContext;

  /** Typed accessor for audio endpoints with Tone.js bridging */
  protected audio: TypedAudio<T>;

  // Audio output endpoint IDs for pan metadata forwarding
  private audioOutputIds: string[] = [];

  constructor(id: string, deps: ToneJsDependencies) {
    super(id, deps);
    this.tone = deps.tone;

    // Create typed proxy for audio endpoints with Tone.js connect bridging
    this.audio = new Proxy({} as TypedAudio<T>, {
      get: (_, key: string) => {
        const metadata = deps.endpointApi.getMetadata(key);
        if (!metadata) throw new Error(`[${this.id}] audio '${key}' does not exist`);
        if (metadata.dataType !== 'audio') {
          throw new Error(`[${this.id}] '${key}' is not an audio endpoint`);
        }

        const tone = this.tone;
        const isInput = metadata.isInput;

        return {
          connect: (node: any) => {
            const gainNode = deps.endpointApi.getGainNode(key);
            if (isInput) {
              // Input: endpoint -> internal node
              tone.connect(gainNode, node);
            } else {
              // Output: internal node -> endpoint
              tone.connect(node, gainNode);
            }
          },
          disconnect: (node: any) => {
            if (isInput) {
              deps.endpointApi.disconnectFromInput(key, node);
            } else {
              deps.endpointApi.disconnectFromOutput(key, node);
            }
          }
        };
      }
    });
  }

  initialize(): void {
    super.initialize();
    // Get audioContext from Tone.js context
    // Tone.js context has .rawContext property
    const toneContext = (this.tone as any).context ?? (this.tone as any).getContext?.();
    if (!toneContext) {
      throw new Error(`[${this.id}] ToneAudioBaseNode.initialize: Tone.js context not available`);
    }
    this.audioContext = toneContext.rawContext ?? toneContext;

    // Discover audio output endpoints for pan forwarding
    for (const id of this.deps.endpointApi.getEndpointIds()) {
      const meta = this.deps.endpointApi.getMetadata(id);
      if (meta?.dataType === 'audio' && meta.isOutput) {
        this.audioOutputIds.push(id);
      }
    }
  }

  /**
   * Forward pan metadata from events to audio outputs.
   * When an event payload contains `pan: {x, y, z}`, push it onto all audio
   * output endpoints so downstream AudioOutputNode can spatialize the audio.
   */
  handleEvent(payload: Payload, endpointId: string, source?: any): void {
    super.handleEvent(payload, endpointId, source);

    if (payload?.pan && this.audioOutputIds.length > 0) {
      for (const outputId of this.audioOutputIds) {
        this.deps.endpointApi.sendOutput(outputId, { value: { pan: payload.pan } });
      }
    }
  }
}
